//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, DocumentDownload, Renew } from '@carbon/icons-react';
import TenantDataService, {
  DiagnosticArchive,
} from '../../utils/TenantDataService';
import { useUserAuth } from '../../UserAuth';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const SupportPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();

  const tenantDataService = useRef(
    new TenantDataService(
      () => user,
      () => isAuthenticated,
    ),
  ).current;

  const [archives, setArchives] = useState<DiagnosticArchive[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  const fetchArchives = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await tenantDataService.getDiagnosticArchives();
      setArchives(Array.isArray(data) ? data : []);
    } catch {
      setError(t('support-fetch-error'));
      setArchives([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantDataService, t]);

  useEffect(() => {
    fetchArchives();
  }, [fetchArchives]);

  const handleDownload = async (filename: string) => {
    setDownloadingFile(filename);
    try {
      const blob = await tenantDataService.downloadDiagnosticArchive(filename);
      const url = window.URL.createObjectURL(blob);
      // Validate the generated URL is a safe blob: scheme before navigating
      if (!url.startsWith('blob:')) {
        console.error('Unexpected URL scheme from createObjectURL');
        window.URL.revokeObjectURL(url);
        return;
      }
      const anchor = document.createElement('a');
      // Break taint chain: reconstruct validated blob URL string (CWE-601)
      anchor.href = String(url);
      anchor.download = filename.replace(/[/\\:\0]/g, '_');
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      setError(t('support-download-error'));
    } finally {
      setDownloadingFile(null);
    }
  };

  const formatTimestamp = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return iso;
    }
  };

  if (isLoading) {
    return (
      <div
        data-testid="support-loading"
        className="flex h-[calc(100vh-70px)] w-full flex-col items-center justify-center"
      >
        <div className="size-12 animate-spin rounded-full border-4 border-gray-200 border-t-brand-blue" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('loading-trans')}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="support-root"
      className="min-h-screen w-full bg-carbon-light dark:bg-carbon-dark"
    >
      <div className="px-4 pb-6 pt-4">
        <div className="rounded-lg bg-white p-6 dark:bg-carbon-darkPanel">
          <div className="mb-2 flex items-center gap-2">
            <DocumentDownload size={20} className="text-brand-nav" />
            <h2 className="text-base font-medium text-carbon-textPrimary dark:text-white">
              {t('diagnostic-archives')}
            </h2>
          </div>
          <p className="mb-6 text-sm text-carbon-textSecondary dark:text-gray-400">
            {t('support-diagnostic-description')}
          </p>

          {error && (
            <div
              role="alert"
              data-testid="support-error"
              className="mb-4 flex items-center justify-between rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            >
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchArchives}
                className="ml-2 gap-1 text-red-700 dark:text-red-400"
              >
                <Renew size={14} />
                {t('support-retry')}
              </Button>
            </div>
          )}

          <div aria-live="polite" className="sr-only">
            {downloadingFile ? t('support-downloading') : ''}
          </div>

          {archives.length === 0 ? (
            <div
              data-testid="support-empty"
              className="flex flex-col items-center justify-center rounded border border-dashed border-gray-300 py-16 dark:border-gray-600"
            >
              <Download size={32} className="mb-3 text-gray-400" />
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('support-no-archives')}
              </p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {t('support-no-archives-description')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('support-filename')}</TableHead>
                  <TableHead>{t('support-timestamp')}</TableHead>
                  <TableHead>{t('support-size')}</TableHead>
                  <TableHead>{t('support-s3-status')}</TableHead>
                  <TableHead className="text-right">
                    {t('support-download')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {archives.map((archive) => (
                  <TableRow
                    key={archive.filename}
                    data-testid={`archive-row-${archive.filename}`}
                  >
                    <TableCell className="font-mono text-xs text-carbon-textPrimary dark:text-gray-200">
                      {archive.filename}
                    </TableCell>
                    <TableCell className="text-sm text-carbon-textSecondary dark:text-gray-400">
                      {formatTimestamp(archive.timestamp)}
                    </TableCell>
                    <TableCell className="text-sm text-carbon-textSecondary dark:text-gray-400">
                      {archive.sizeDisplay}
                    </TableCell>
                    <TableCell>
                      {archive.uploaded ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {t('support-uploaded')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                          {t('support-not-uploaded')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`download-btn-${archive.filename}`}
                        disabled={downloadingFile === archive.filename}
                        onClick={() => handleDownload(archive.filename)}
                        className="gap-1"
                      >
                        <Download size={14} />
                        {downloadingFile === archive.filename
                          ? t('support-downloading')
                          : t('support-download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
